import React, { useCallback, useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import {
  CustomSectionTitle,
  CustomSingleSelect,
  CustomLoading,
  FixedActionBar,
  CustomFormNavigationGuard,
} from "@ippon/shared-components";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import type { AppDispatch, RootState } from "@/store";
import {
  fetchDetailedClient,
  updateFiscalClosingMonth,
} from "@/store/clientSlice";
import { useGlobalToast } from "@/hooks/useGlobalToast";
import { getClientID } from "@/utils/authUtils";
import InitialLoadError from "@/components/ErrorState/InitialLoadError";
import type { ApiErrorPayload, ApiErrorType } from "@/utils/apiErrorPayload";
import {
  shouldShowInitialLoadError,
  useStaleInitialLoadErrorToast,
} from "@/utils/initialLoadError";

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  label: `${i + 1}月`,
  value: String(i + 1),
}));

const FiscalClosingSetting: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { showToast } = useGlobalToast();
  const detailed = useSelector((state: RootState) => state.clients?.detailed);
  const clientId = getClientID();
  const currentMonth: number | undefined =
    detailed?.data?.fiscal_closing_month ?? undefined;

  const {
    control,
    reset,
    handleSubmit,
    formState: { isDirty },
  } = useForm<{ fiscal_closing_month: string }>({
    defaultValues: {
      fiscal_closing_month: currentMonth ? String(currentMonth) : "3",
    },
  });

  // 既存値を反映 (取得後 / 別画面から戻ってきたとき)。
  // reset で defaultValues も同期させて isDirty が立たないようにする。
  useEffect(() => {
    if (typeof currentMonth === "number") {
      reset({ fiscal_closing_month: String(currentMonth) });
    }
  }, [currentMonth, reset]);

  const [initialLoadError, setInitialLoadError] =
    useState<ApiErrorType | null>(null);
  const hasCachedClientData = Boolean(detailed?.data);

  const fetchDetail = useCallback(async () => {
    if (!clientId) return;
    setInitialLoadError(null);
    const result = await dispatch(fetchDetailedClient(clientId));
    if (!fetchDetailedClient.fulfilled.match(result)) {
      const payload = result.payload as ApiErrorPayload | undefined;
      setInitialLoadError(payload?.errorType ?? "unknown");
    }
  }, [clientId, dispatch]);

  useEffect(() => {
    void fetchDetail();
  }, [fetchDetail]);

  useStaleInitialLoadErrorToast({
    errorType: initialLoadError,
    hasCachedData: hasCachedClientData,
    dedupeKey: "fiscal-closing",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = handleSubmit(async (values) => {
    if (!clientId) {
      showToast({
        message: "クライアントIDが取得できません",
        type: "error",
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const newMonth = Number(values.fiscal_closing_month);
      await dispatch(
        updateFiscalClosingMonth({
          id: clientId,
          fiscal_closing_month: newMonth,
        }) as any,
      ).unwrap();
      // dirty 状態を解除するため defaultValues も更新
      reset({ fiscal_closing_month: String(newMonth) });
      showToast({ message: "決算月を更新しました", type: "success" });
    } catch {
      showToast({ message: "決算月の更新に失敗しました", type: "error" });
    } finally {
      setIsSubmitting(false);
    }
  });

  if (detailed?.loading && !detailed?.data) {
    return <CustomLoading loading={true} />;
  }

  if (shouldShowInitialLoadError(initialLoadError, hasCachedClientData)) {
    return (
      <InitialLoadError errorType={initialLoadError} onRetry={fetchDetail} />
    );
  }

  return (
    <CustomFormNavigationGuard hasFormChanged={isDirty}>
      {() => (
    <Box sx={{ pt: 3, pb: 12 }}>
      <CustomSectionTitle title="決算月" size="large" />
      <Typography
        variant="body2"
        sx={{
          mb: 2,
          mt: 1,
          color: "#666",
          fontSize: { xs: 12, sm: 14 },
          lineHeight: 1.6,
        }}
      >
        会社の決算月を設定してください。会計年度はこの月の翌月から始まります。
      </Typography>
      <Box sx={{ mt: 2, maxWidth: 480 }}>
        <CustomSingleSelect
          label="決算月"
          name="fiscal_closing_month"
          control={control}
          options={MONTH_OPTIONS}
          placeholder="選択してください"
          labelWidth="120px"
          inputWidth="100%"
          isRequired
        />
      </Box>
      <FixedActionBar
        submitLabel="更新"
        isLoading={isSubmitting}
        disabled={!isDirty || isSubmitting}
        onSubmit={() => void onSubmit()}
      />
    </Box>
      )}
    </CustomFormNavigationGuard>
  );
};

export default FiscalClosingSetting;
